import { round } from "@ugg/shared/utils/math";
import { RoleC, RoleN } from "@ugg/shared/utils/role-helpers";
import { RegionC, RegionN } from "@ugg/shared/utils/region-helpers";
import { RankC, RankN } from "@ugg/shared/utils/rank-helpers";
import { getKey, getData } from "./common";

import {
  KeystonePairs,
  Tree,
  SummonerSpells,
  StartingItems,
  CoreItemsWithBoots,
  Skills,
  Boots,
  CoreItems,
  AdvBuild,
} from "@ugg/shared/interfaces/champions/adv-builds.interface";

export function getAdvBuilds(
  data: ReturnType<typeof cleanAdvBuildsData>,
  params: {
    queueType: string;
    region: string;
    rank?: string;
    role?: string;
  },
) {
  return getData(data, params);
}

export interface AdvBuildInfo {
  keystone_pairs: Array<{
    keystone_id: KeystonePairs[0];
    primary_style: KeystonePairs[4];
    sub_style: KeystonePairs[1];
    wins: KeystonePairs[2];
    matches: KeystonePairs[3];
    win_rate: number;
    runes: Array<{
      keystone_id: KeystonePairs[0];
      primary_style: Tree[0];
      sub_style: Tree[1];
      wins: Tree[3];
      matches: Tree[4];
      win_rate: number;
      active_perks: Tree[2];
    }>;
  }>;
  summoner_spells: Array<{
    ids: SummonerSpells[0];
    wins: SummonerSpells[1];
    matches: SummonerSpells[2];
    win_rate: number;
  }>;
  starting_items: Array<{
    ids: StartingItems[0];
    wins: StartingItems[1];
    matches: StartingItems[2];
    win_rate: number;
  }>;
  core_items: Array<{
    ids: CoreItems[0];
    wins: CoreItems[1];
    matches: CoreItems[2];
    win_rate: number;
  }>;
  skills: Array<{
    ids: Skills[0];
    wins: Skills[1];
    matches: Skills[2];
    win_rate: number;
  }>;
  boots: Array<{
    id: Boots[0];
    wins: Boots[1];
    matches: Boots[2];
    win_rate: number;
  }>;
  core_items_with_boots: Array<{
    ids: CoreItemsWithBoots[0];
    wins: CoreItemsWithBoots[1];
    matches: CoreItemsWithBoots[2];
    win_rate: number;
  }>;
}

export function cleanAdvBuildsData(data: AdvBuild) {
  const cleanData: Record<string, AdvBuildInfo> = {};

  const regionKeys: RegionN[] = Object.keys(data || {}).map(Number);
  for (const regionId of regionKeys) {
    const regionData = data[regionId];
    const rankKeys: RankN[] = Object.keys(regionData || {}).map(Number);

    for (const rankId of rankKeys) {
      const rankData = regionData[rankId];
      const roleKeys: RoleN[] = Object.keys(rankData || {}).map(Number);

      for (const roleId of roleKeys) {
        const roleData = rankData[roleId];

        const [keystonePairs, summonerSpells, startingItems, coreItemsWithBoots, skills, boots, coreItems] = roleData;

        // Keystone Pairs
        const keystone_pairs = keystonePairs.map((pair) => {
          const runes = pair[5].map((tree) => {
            return {
              keystone_id: pair[0],
              primary_style: tree[0],
              sub_style: tree[1],
              wins: tree[3],
              matches: tree[4],
              win_rate: round((tree[3] / tree[4]) * 100, 2),
              active_perks: tree[2],
            };
          });

          return {
            keystone_id: pair[0],
            primary_style: pair[4],
            sub_style: pair[1],
            wins: pair[2],
            matches: pair[3],
            win_rate: round((pair[2] / pair[3]) * 100, 2),
            runes: runes,
          };
        });

        // Summoner Spells
        const summoner_spells = summonerSpells.map((el) => ({
          ids: el[0],
          wins: el[1],
          matches: el[2],
          win_rate: round((el[1] / el[2]) * 100, 2),
        }));

        // Starting Items
        const starting_items = startingItems.map((el) => ({
          ids: el[0],
          wins: el[1],
          matches: el[2],
          win_rate: round((el[1] / el[2]) * 100, 2),
        }));

        // Core Items
        const core_items = coreItems.map((el) => ({
          ids: el[0],
          wins: el[1],
          matches: el[2],
          win_rate: round((el[1] / el[2]) * 100, 2),
        }));

        // Skills
        const formattedSkills = skills.map((el) => ({
          ids: el[0],
          wins: el[1],
          matches: el[2],
          win_rate: round((el[1] / el[2]) * 100, 2),
        }));

        // Boots
        const formattedBoots = boots.map((el) => ({
          id: el[0],
          wins: el[1],
          matches: el[2],
          win_rate: round((el[1] / el[2]) * 100, 2),
        }));

        // Core Items with Boots
        const core_items_with_boots = coreItemsWithBoots.map((el) => ({
          ids: el[0],
          wins: el[1],
          matches: el[2],
          win_rate: round((el[1] / el[2]) * 100, 2),
        }));

        const advBuild = {
          keystone_pairs,
          summoner_spells,
          starting_items,
          core_items,
          skills: formattedSkills,
          boots: formattedBoots,
          core_items_with_boots,
        };

        const key = getKey(RegionC.convertToString(regionId), RankC.convertToString(rankId), RoleC.convertToString(roleId));

        cleanData[key] = advBuild;
      }
    }
  }

  return cleanData;
}
